import { DateCalendar } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateRangeCalendarContext, RangePickerDay } from './picker-day';
import type {
  DateRangeCalendarInternalValue,
  DateRangeCalendarProps,
} from './types';

export type { DateRangeCalendarProps } from './types';

export const DateRangeCalendar = ({
  value,
  onChange,
  maxDateRange,
  ...props
}: DateRangeCalendarProps) => {
  const lastSelectedDayIndexRef = useRef<number>(1);
  const [lastSelectedDayIndex, setLastSelectedDayIndex] = useState(1);
  const [internalValue, setInternalValue] =
    useState<DateRangeCalendarInternalValue>(value);
  const [rangePreviewDay, setRangePreviewDay] = useState<Dayjs>();

  const onDaySelect = useCallback(
    (_e: React.MouseEvent, day: Dayjs) => {
      let newValue = (
        internalValue ? [...internalValue] : []
      ) as NonNullable<DateRangeCalendarInternalValue>;

      if (lastSelectedDayIndexRef.current === 1) {
        lastSelectedDayIndexRef.current = 0;
        setLastSelectedDayIndex(0);

        if (newValue[1] && day.isAfter(newValue[1], 'day')) {
          newValue = [day];
        } else {
          newValue[0] = day;

          if (maxDateRange) {
            const maxSecondDate = maxDateRange(day);
            if (newValue?.[1]?.isAfter(maxSecondDate, 'day')) {
              newValue[1] = maxSecondDate;
            }
          }
        }
      } else if (newValue[0] && day.isBefore(newValue[0], 'day')) {
        lastSelectedDayIndexRef.current = 0;
        setLastSelectedDayIndex(0);
        newValue = [day];
      } else {
        lastSelectedDayIndexRef.current = 1;
        setLastSelectedDayIndex(1);
        newValue[1] = day;
      }

      setInternalValue(newValue);

      if (newValue.length === 2) {
        onChange(newValue);
      }
    },
    [internalValue, value],
  );

  const contextValue = useMemo(
    () => ({
      pickerValue: internalValue,
      onRangeDaySelect: onDaySelect,
      lastSelectedDayIndex,
      rangePreviewDay,
      setRangePreviewDay,
    }),
    [
      internalValue,
      onDaySelect,
      lastSelectedDayIndex,
      rangePreviewDay,
      setRangePreviewDay,
    ],
  );

  useEffect(() => {
    if (value) {
      setInternalValue(value);
    }
  }, [value]);

  return (
    <DateRangeCalendarContext.Provider value={contextValue}>
      <DateCalendar
        slots={{
          day: RangePickerDay,
        }}
        referenceDate={internalValue?.[0]}
        {...props}
      />
    </DateRangeCalendarContext.Provider>
  );
};
